import { useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, CircleDashed } from "lucide-react";
import {
  formatearRespuestas,
  respuestaCompleta,
  respuestasIniciales,
  type Pregunta,
  type Respuesta,
  type Respuestas,
} from "@/lib/preguntas";

/**
 * Formulario de opciones cerradas: el formulario SUGERIDO que el backend deriva
 * de la prosa del agente, y tambien el que construye una tool a partir de DATOS
 * (los servicios relacionados de `presupuesto`). Este segundo puede traer
 * opciones premarcadas (`marcada`: el formulario abre con ellas y se puede enviar
 * sin tocar nada) y preguntas que admiten no marcar nada (`permite_vacia`: no
 * cuentan como pendientes y, vacias, se envian como "Cabecera: Ninguno").
 *
 * Es agnóstico del runtime: recibe `onEnviar(texto)` y quien lo monta decide cómo
 * mandar el texto (hoy solo el chat principal, con `useThreadRuntime().append`;
 * el widget embebido NO lo pinta, por decisión: el backend se salta la extracción
 * en runs de widget). "Otra" la añade siempre este componente: el backend no la
 * configura.
 *
 * `onEnviar` PUEDE devolver una promesa: solo pasamos a modo lectura cuando el
 * envío termina bien (resolución, o retorno sync sin lanzar). Si falla, el
 * formulario sigue activo con lo marcado — el usuario no reescribe nada. Ojo con
 * el caso del chat principal: `ThreadRuntime.append` devuelve `void` y se traga
 * su promesa, así que allí un fallo de red NO llega aquí y el aviso lo pinta el
 * propio mensaje del thread (`chat.messageFailed`).
 *
 * Semántica de "Otra": en elección única es excluyente con las opciones (abrirla
 * vacía la selección y marcar una opción la cierra), así el texto enviado es solo
 * lo que el usuario ve marcado. En elección múltiple convive con las opciones.
 */
interface Props {
  preguntas: Pregunta[];
  modo: "activo" | "lectura";
  onEnviar: (texto: string) => void | Promise<unknown>;
}

const OTRA = "__otra__";

function vacia(): Respuesta {
  return { seleccion: [], otra: "" };
}

const CLASE_OPCION =
  "flex cursor-pointer items-start gap-2 rounded-lg border px-3 py-2 transition-colors";
const CLASE_MARCADA = "border-[var(--primary)] bg-[var(--muted)]";
const CLASE_SIN_MARCAR = "border-[var(--border)] hover:bg-[var(--muted)]/60";

export default function OpcionesForm({ preguntas, modo, onEnviar }: Props) {
  const { t } = useTranslation();
  // Todos los hooks van ANTES del return condicional de modo lectura.
  // Arranca con lo que el backend dejo premarcado (vacio en los formularios
  // derivados de la prosa, que no marcan nada).
  const [respuestas, setRespuestas] = useState<Respuestas>(() => respuestasIniciales(preguntas));
  const [otraAbierta, setOtraAbierta] = useState<Record<string, boolean>>({});
  const [activa, setActiva] = useState(0);
  const [enviado, setEnviado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [previewIdx, setPreviewIdx] = useState(0);
  // Pestañas que el usuario ya ha visto (la primera se ve al abrir). Una pregunta
  // `permite_vacia` cuenta como respondida desde el principio —vacia es
  // "ninguno"—, pero su pestaña no puede decir "Respondido" antes de que el
  // usuario la haya mirado: estaria afirmando una decision que nadie tomo.
  const [visitadas, setVisitadas] = useState<Set<number>>(() => new Set([0]));
  // Refs de las pestañas para mover el foco con las flechas (roving tabindex).
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  // Prefijo de los ids DOM de pestañas/panel: dos formularios del mismo thread
  // pueden repetir un id de pregunta (p. ej. `direccion` preguntada dos veces).
  const uid = useId();

  const lectura = modo === "lectura" || enviado;
  const pendientes = useMemo(
    () => preguntas.filter((p) => !respuestaCompleta(respuestas[p.id], p)).length,
    [preguntas, respuestas],
  );

  if (lectura) {
    // "Respondido" SOLO si este formulario envió las respuestas (`enviado`). Un
    // formulario que dejó de estar activo porque la conversación siguió (modo
    // lectura desde fuera) NO sabe si el usuario contestó en prosa, lo ignoró o
    // cambió de tema: se muestra cerrado, neutro, sin afirmar nada.
    const etiqueta = enviado ? t("preguntas.respondido") : t("preguntas.cerrado");
    return (
      <div className="my-2 rounded-xl border border-[var(--border)] bg-[var(--muted)]/40 px-3 py-2 text-sm">
        {preguntas.map((p) => (
          <div key={p.id} className="flex items-center gap-2 py-0.5 text-[var(--muted-foreground)]">
            {enviado ? (
              <Check size={14} className="shrink-0 text-green-600" />
            ) : (
              <CircleDashed size={14} className="shrink-0" />
            )}
            <span className="truncate">{p.pregunta}</span>
            <span className="ml-auto shrink-0 text-xs">{etiqueta}</span>
          </div>
        ))}
      </div>
    );
  }

  const idx = Math.min(activa, preguntas.length - 1);
  const p = preguntas[idx];
  const r = respuestas[p.id] ?? vacia();
  const conPreview = p.opciones.some((o) => !!o.vista_previa);
  const opcional = p.permite_vacia === true;
  // Una pregunta que admite "ninguno" va SIEMPRE con checkboxes, tambien si es de
  // eleccion unica: un radio marcado no se puede desmarcar, y dejarla vacia es
  // una respuesta valida. La eleccion unica la mantiene `elegir`.
  const inputType = p.multiple || opcional ? "checkbox" : "radio";
  const todasVisitadas = preguntas.every((_, i) => visitadas.has(i));
  // Con una sola pregunta no hay tablist: un tabpanel suelto se anuncia raro.
  const conPestanas = preguntas.length > 1;

  // Único punto de cambio de pregunta activa: la vista previa vuelve a la primera opción.
  const irA = (i: number) => {
    setActiva(i);
    setPreviewIdx(0);
    setVisitadas((prev) => (prev.has(i) ? prev : new Set(prev).add(i)));
  };

  const saltarASiguiente = (desde: number, siguiente: Respuestas) => {
    const next = preguntas.findIndex((q, i) => i > desde && !respuestaCompleta(siguiente[q.id], q));
    if (next >= 0) irA(next);
  };

  const elegir = (etiqueta: string) => {
    const actual = respuestas[p.id] ?? vacia();
    let nueva: Respuesta;
    if (p.multiple) {
      const seleccion = actual.seleccion.includes(etiqueta)
        ? actual.seleccion.filter((e) => e !== etiqueta)
        : [...actual.seleccion, etiqueta];
      nueva = { ...actual, seleccion };
    } else if (opcional && actual.seleccion.includes(etiqueta)) {
      // Elección única que admite "ninguno": pulsar la marcada la desmarca.
      nueva = { ...actual, seleccion: [] };
    } else {
      // Elección única: marcar una opción cierra "Otra" y descarta su texto.
      nueva = { seleccion: [etiqueta], otra: "" };
      if (otraAbierta[p.id]) setOtraAbierta({ ...otraAbierta, [p.id]: false });
    }
    const siguiente = { ...respuestas, [p.id]: nueva };
    setRespuestas(siguiente);
    if (!p.multiple && nueva.seleccion.length > 0) saltarASiguiente(idx, siguiente);
  };

  const toggleOtra = () => {
    const abierta = !otraAbierta[p.id];
    setOtraAbierta({ ...otraAbierta, [p.id]: abierta });
    if (!abierta) {
      setRespuestas({ ...respuestas, [p.id]: { ...r, otra: "" } });
    } else if (!p.multiple) {
      // Elección única: abrir "Otra" vacía la opción marcada (son excluyentes).
      setRespuestas({ ...respuestas, [p.id]: { ...r, seleccion: [] } });
    }
  };

  const enviar = async () => {
    if (pendientes > 0 || enviando) return;
    // Solo pasamos a modo lectura si el envío termina bien: si falla, el
    // formulario sigue activo y el usuario no pierde lo que había marcado.
    setEnviando(true);
    try {
      await onEnviar(
        formatearRespuestas(preguntas, respuestas, t("preguntas.ademas"), t("preguntas.ninguno")),
      );
      setEnviado(true);
    } catch (err) {
      console.error("OpcionesForm: el envio fallo", err);
    } finally {
      setEnviando(false);
    }
  };

  // Flechas en la lista de pestañas: mueven la pregunta activa y el foco.
  const teclaTablist = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const next = e.key === "ArrowRight" ? Math.min(idx + 1, preguntas.length - 1) : Math.max(idx - 1, 0);
    if (next === idx) return;
    irA(next);
    tabRefs.current[next]?.focus();
  };

  return (
    <div className="my-2 rounded-xl border border-[var(--border)] bg-[var(--background)] text-sm shadow-sm">
      {conPestanas && (
        <div
          role="tablist"
          onKeyDown={teclaTablist}
          className="flex gap-1 border-b border-[var(--border)] px-2 pt-2"
        >
          {preguntas.map((q, i) => {
            const qOpcional = q.permite_vacia === true;
            // Una opcional sin visitar queda NEUTRA (ni pendiente ni respondida):
            // no bloquea el envio, pero tampoco afirma que el usuario la reviso.
            const sinRevisar = qOpcional && !visitadas.has(i);
            const hecha = !sinRevisar && respuestaCompleta(respuestas[q.id], q);
            const titulo = sinRevisar
              ? t("preguntas.pestanaOpcional")
              : hecha
                ? t("preguntas.respondido")
                : t("preguntas.pestanaPendiente");
            // Cuántas hay marcadas, visible desde cualquier pestaña: lo premarcado
            // de una pestaña que no se ha abierto tambien se enviara.
            const marcadas = qOpcional ? (respuestas[q.id]?.seleccion.length ?? 0) : 0;
            return (
              <button
                key={q.id}
                ref={(el) => {
                  tabRefs.current[i] = el;
                }}
                type="button"
                role="tab"
                id={`${uid}-tab-${q.id}`}
                aria-controls={`${uid}-panel-${q.id}`}
                aria-selected={i === idx}
                tabIndex={i === idx ? 0 : -1}
                onClick={() => irA(i)}
                title={titulo}
                className={`flex items-center gap-1 rounded-t-md px-3 py-1.5 text-xs font-medium transition-colors ${
                  i === idx
                    ? "border-b-2 border-[var(--primary)] text-[var(--foreground)]"
                    : "text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
                }`}
              >
                {hecha ? <Check size={12} className="text-green-600" /> : <CircleDashed size={12} />}
                {marcadas > 0 ? `${q.cabecera} · ${marcadas}` : q.cabecera}
              </button>
            );
          })}
        </div>
      )}

      <div
        role={conPestanas ? "tabpanel" : undefined}
        id={conPestanas ? `${uid}-panel-${p.id}` : undefined}
        aria-labelledby={conPestanas ? `${uid}-tab-${p.id}` : undefined}
        className="px-4 py-3"
      >
        <p id={`${uid}-pregunta-${p.id}`} className="mb-3 font-medium text-[var(--foreground)]">
          {p.pregunta}
        </p>

        <div className={conPreview ? "grid gap-3 md:grid-cols-2" : ""}>
          {/* Un formulario construido desde datos puede traer hasta 20 opciones por
              pregunta: con muchas, el panel hace scroll en vez de empujar el chat. */}
          <div
            role="group"
            aria-labelledby={`${uid}-pregunta-${p.id}`}
            className={`flex flex-col gap-1.5${p.opciones.length > 6 ? " max-h-80 overflow-y-auto" : ""}`}
          >
            {p.opciones.map((o, i) => {
              const marcada = r.seleccion.includes(o.etiqueta);
              return (
                <label
                  key={`${i}-${o.etiqueta}`}
                  onMouseEnter={() => setPreviewIdx(i)}
                  onFocus={() => setPreviewIdx(i)}
                  className={`${CLASE_OPCION} ${marcada ? CLASE_MARCADA : CLASE_SIN_MARCAR}`}
                >
                  <input
                    type={inputType}
                    name={`${p.id}-opcion`}
                    checked={marcada}
                    onChange={() => elegir(o.etiqueta)}
                    className="mt-0.5 accent-[var(--primary)]"
                  />
                  <span className="flex min-w-0 flex-col">
                    <span className="flex items-center gap-2 font-medium text-[var(--foreground)]">
                      {o.etiqueta}
                      {o.recomendada && (
                        <span className="rounded-full bg-[var(--primary)]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--primary)]">
                          {t("preguntas.recomendada")}
                        </span>
                      )}
                    </span>
                    {o.descripcion && (
                      <span className="text-xs text-[var(--muted-foreground)]">{o.descripcion}</span>
                    )}
                  </span>
                </label>
              );
            })}

            <label className={`${CLASE_OPCION} ${otraAbierta[p.id] ? CLASE_MARCADA : CLASE_SIN_MARCAR}`}>
              <input
                type={inputType}
                name={`${p.id}-opcion`}
                value={OTRA}
                checked={!!otraAbierta[p.id]}
                onChange={toggleOtra}
                className="mt-0.5 accent-[var(--primary)]"
              />
              <span className="font-medium text-[var(--foreground)]">{t("preguntas.otra")}</span>
            </label>
            {otraAbierta[p.id] && (
              <input
                type="text"
                autoFocus
                value={r.otra}
                onChange={(e) => setRespuestas({ ...respuestas, [p.id]: { ...r, otra: e.target.value } })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    // Enter es un atajo: con pestañas sin visitar enviaria tambien
                    // lo premarcado que el usuario aun no ha visto. El boton sigue
                    // disponible para quien quiera enviar igualmente.
                    if (todasVisitadas) void enviar();
                  }
                }}
                placeholder={t("preguntas.otraPlaceholder")}
                className="rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm text-[var(--foreground)] outline-none focus:border-[var(--primary)]"
              />
            )}
          </div>

          {conPreview && (
            <pre className="max-h-64 overflow-auto rounded-lg border border-[var(--border)] bg-[var(--muted)] p-3 font-mono text-xs text-[var(--foreground)]">
              {p.opciones[Math.min(previewIdx, p.opciones.length - 1)]?.vista_previa ?? ""}
            </pre>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-[var(--border)] px-4 py-2">
        {/* El hint es la vía de escape (responder en prosa): visible SIEMPRE mientras el formulario está activo. */}
        <span className="text-xs text-[var(--muted-foreground)]">{t("preguntas.hint")}</span>
        <button
          type="button"
          onClick={enviar}
          disabled={pendientes > 0 || enviando}
          className="rounded-lg bg-[var(--primary)] px-3 py-1.5 text-xs font-semibold text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
        >
          {pendientes > 0 ? t("preguntas.faltan", { count: pendientes }) : t("preguntas.enviar")}
        </button>
      </div>
    </div>
  );
}
